import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth.jsx';
import { Badge, Modal, ErrorNote } from '../components/ui.jsx';

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = { employeeId: '', date: today(), severity: 'LOW', title: '', description: '', signature: '' };

export default function Incidents() {
  const { user, can } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [severity, setSeverity] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api('/incidents' + (severity ? `?severity=${severity}` : ''))
    .then(setIncidents).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [severity]);
  useEffect(() => { api('/employees').then(setEmployees).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api('/incidents', { method: 'POST', body: form });
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="filter-bar">
        <div className="segmented">
          {['', 'HIGH', 'MEDIUM', 'LOW'].map((s) => (
            <button key={s || 'all'} className={severity === s ? 'on' : ''} onClick={() => setSeverity(s)}>
              {s ? s[0] + s.slice(1).toLowerCase() : 'All'}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        {can('incidents.manage') && (
          <button className="btn btn-primary" onClick={() => { setOpen(true); setError(''); }}>+ Log Incident</button>
        )}
      </div>
      {!open && <ErrorNote error={error} />}

      <div className="incident-cards">
        {incidents.map((i) => (
          <div key={i.id} className="incident-card">
            <div className={`incident-rail ${i.severity}`} />
            <div className="incident-left">
              <div className="incident-title">{i.title} <Badge kind={i.severity}>{i.severity}</Badge></div>
              <div className="incident-desc">{i.description}</div>
              <div className="incident-meta">
                <Link to={`/employees/${i.employee.id}`} className="fw-bold" style={{ color: 'var(--blue)' }}>
                  {i.employee.name} ({i.employee.code})
                </Link>
                <span>{i.employee.site.name}</span>
                <span>{new Date(i.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                {i.reportedBy && <span>Reported by {i.reportedBy.name}</span>}
                {i.signature && <span>✍ Signed: {i.signature}</span>}
              </div>
            </div>
          </div>
        ))}
        {incidents.length === 0 && <div className="empty-state"><p>No incidents on record.</p></div>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Log Incident / Warning">
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group"><label>Employee</label>
              <select className="form-select" value={form.employeeId} onChange={set('employeeId')} required>
                <option value="" disabled>Select employee…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.code} — {e.name}</option>)}
              </select></div>
            <div className="form-group"><label>Date</label>
              <input type="date" className="form-input" value={form.date} max={today()} onChange={set('date')} required /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Severity</label>
              <select className="form-select" value={form.severity} onChange={set('severity')}>
                <option value="LOW">Low — verbal warning</option>
                <option value="MEDIUM">Medium — formal warning</option>
                <option value="HIGH">High — serious violation</option>
              </select></div>
            <div className="form-group"><label>Title</label>
              <input className="form-input" value={form.title} onChange={set('title')}
                placeholder="e.g. Safety violation" required /></div>
          </div>
          <div className="form-group"><label>Description</label>
            <textarea className="form-textarea" value={form.description} onChange={set('description')}
              placeholder="What happened, where, and any action taken…" required /></div>
          {form.severity === 'HIGH' && (
            <div className="form-group">
              <label>Digital Signature (required for high severity)</label>
              <input className="form-input" value={form.signature} onChange={set('signature')}
                placeholder={`Type your full name — ${user.name}`} required />
            </div>
          )}
          <ErrorNote error={error} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Log Incident'}</button>
          </div>
        </form>
      </Modal>
    </>
  );
}
